import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useUrlState } from '@/hooks/useUrlState'
import { customerService } from '@/services/customer.service'
import type { DraftRecipient } from './useOfferWizardDraft'

/** The wizard draft's recipient and its setter, as `useOfferWizardDraft` returns them. */
export interface RecipientDraft {
	recipient: DraftRecipient | null
	setRecipient: (recipient: DraftRecipient | null) => void
}

/**
 * Seeds the offer wizard's recipient from a `?customerId=` deep link — the Customers list's
 * "Start an offer" row action (TO-697 customers-workspace-list REQ-006).
 *
 * @remarks
 * The customer is resolved through `GET /customers/{id}` only while the draft has no
 * recipient, so a recipient already in the URL (a reload, or one picked by hand) always wins.
 * Once the lookup settles, `customerId` is dropped from the URL — in the same replace-history
 * write as the recipient on success — so it can never re-seed later. A failed lookup (404 for
 * an unknown or other-tenant id, 403, network) seeds nothing: the plain recipient step is the
 * recovery path.
 */
export function useCustomerRecipientPreseed({ recipient, setRecipient }: RecipientDraft): void {
	const [customerId, setCustomerId] = useUrlState<string>('customerId', { defaultValue: '' })
	const lookup = useQuery({
		queryKey: ['offers', 'recipient-preseed', customerId],
		queryFn: () => customerService.get(customerId),
		enabled: customerId !== '' && recipient === null,
		retry: false,
	})

	useEffect(() => {
		if (customerId === '') return
		if (recipient !== null) {
			setCustomerId('')
			return
		}
		if (lookup.isSuccess) {
			setRecipient({ id: lookup.data.customerId, name: lookup.data.fullName })
			setCustomerId('')
		} else if (lookup.isError) {
			setCustomerId('')
		}
	}, [customerId, recipient, lookup.isSuccess, lookup.isError, lookup.data, setRecipient, setCustomerId])
}
